import type { Me } from '@myrewards/contracts';
import type { QueryClient } from '@tanstack/react-query';
import { redirect, type LoaderFunctionArgs, type RouteObject } from 'react-router';
import { budgetsQuery, meQuery, mySettingsQuery } from '../api';
import {
  letterTemplatesQuery,
  myNotificationsQuery,
  sectorSetupListQuery,
  settingQuery,
  settingsAccessQuery,
  spotWorkflowSetupQuery,
} from '../api/settings';
import { DISPLAY_ID } from '../api/feedbackAdmin';
import { letterCoverageQuery } from '../api/letter-coverage';
import {
  myFeedbackItemQuery,
  myFeedbackKeys,
  myFeedbackQuery,
  myFeedbackView,
} from '../api/myFeedback';
import { opensPage } from '../app/access';
import type { RouteHandle } from '../app/RootLayout';
import type { PageSearch } from '../search/pages';
import { requireMe } from '../session/loaders';
import { canOpenArea, holds, type SettingsAreaId } from './nav';
import { letterCellHref } from './letters/links';
import { adminLoad } from '../admin/routes';
import { notFound, orNotFound } from '../ops/routes';
import { loaderPaging, skipRevalidationFor } from '../ui';
import { movedTo } from '../manage/moved';
import { MANAGE_PAGE } from '../manage/pages';

// Settings owned by roles (Phase 7) and Manage's Executive group's Budget pools and Values (X4, X5).
// Each screen loads its code on first use (APP-008); loaders warm the data the screen opens with,
// and never fail the page for a card the person may not see.

const quietly = (p: Promise<unknown>) => p.catch(() => null);

/**
 * Warms a setting's read only when this person may read it (QA-025), so a loader never makes a
 * request the server refuses. If the list can't be had, it warms anyway, quietly.
 */
async function warm(queryClient: QueryClient, key: string, read: () => Promise<unknown>) {
  const access = await queryClient.query(settingsAccessQuery).catch(() => null);
  if (access !== null && !access.readable.includes(key)) return;
  await quietly(read());
}

/** The page's Help, and how it shows in Search & jump (APP-022) for the people who can open it. */
function help(pageLabel: string, topicIds: readonly string[], search?: PageSearch): RouteHandle {
  return { help: { pageLabel, topicIds }, ...(search !== undefined && { search }) };
}

// Executives reach Budget pools and Values under Manage's Executive group; search offers each
// screen once.
const settingsArea =
  (area: SettingsAreaId, unlessExecutive = false) =>
  (me: Me): boolean =>
    canOpenArea(me, area) && !(unlessExecutive && holds(me, 'executive'));
/** Executive pages: the Executives', and System Admins see every page (APP-134). */
const executive = (me: Me): boolean => opensPage(me, ['executive']);

/** A rows-per-page choice saved and dropped from the address needs no fresh read (APP-135). */
const sizeSaved = skipRevalidationFor([], { sizeRemoved: ['size'] });

// APP-138 §D8: My feedback and each report have Help of their own (the `my-feedback` page key).
const myFeedbackHelp = help('My feedback', [
  'my-feedback',
  'my-feedback-replies',
  'my-feedback-assistant',
]);

/** The Settings section: `/settings` (My preferences) and each area a role owns. */
export function settingsRoutes(queryClient: QueryClient): RouteObject[] {
  return [
    ...['settings/help-catalog', 'settings/help-catalog/new', 'settings/help-catalog/:id'].map(
      (path): RouteObject => ({
        path,
        loader: async ({ request }: LoaderFunctionArgs) => {
          await requireMe(queryClient, request);
          return null;
        },
        lazy: async () =>
          path === 'settings/help-catalog'
            ? { Component: (await import('../help-catalog/CatalogPage')).CatalogPage }
            : { Component: (await import('../help-catalog/EditorPage')).EditorPage },
        handle: help('Help catalog', ['search'], {
          label: 'Help catalog',
          meta: 'Find, edit and publish Help articles',
          visible: settingsArea('help-catalog'),
        }),
      }),
    ),
    ...['settings/messages', 'settings/messages/:key'].map((path): RouteObject => ({
      path,
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requireMe(queryClient, request);
        return null;
      },
      lazy: async () =>
        path === 'settings/messages'
          ? { Component: (await import('./messages/MessagesPage')).MessagesPage }
          : { Component: (await import('./messages/MessagePage')).MessagePage },
      handle: help('Messages', ['message-wording', 'email-frame'], {
        label: 'Messages',
        meta: 'See and change the wording of every email, bell line and Teams card',
        visible: settingsArea('messages'),
      }),
    })),
    {
      path: 'settings/system/preferences',
      loader: ({ request }: LoaderFunctionArgs) =>
        adminLoad(queryClient, request, () => Promise.resolve()),
      lazy: async () => ({
        Component: (await import('./AssistedPreferencesPage')).AssistedPreferencesPage,
      }),
      handle: help('Settings · People’s preferences', ['preferences', 'audit-log'], {
        label: 'People’s preferences',
        meta: 'Help someone with their preferences, with every change audited',
        visible: (me) => holds(me, 'system_admin'),
      }),
    },
    {
      path: 'settings',
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requireMe(queryClient, request);
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router loaders redirect by throwing a Response.
        throw redirect('/settings/me');
      },
    },
    {
      path: 'settings/me',
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requireMe(queryClient, request);
        await queryClient.query(mySettingsQuery);
        // Notify me about (APP-092): warmed, never failing the page.
        await quietly(queryClient.query(myNotificationsQuery));
        return null;
      },
      lazy: async () => {
        const { MySettingsPage } = await import('./MySettingsPage');
        return { Component: MySettingsPage };
      },
      handle: help('My preferences', ['preferences', 'search'], {
        label: 'My preferences',
        meta: 'Your name, notifications, the page header, motion and accessibility',
      }),
    },
    {
      path: 'settings/me/feedback',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        // Nothing live is read while previewing; the shell shows the page's sample layout.
        if (me.preview !== null) return null;
        const params = new URL(request.url).searchParams;
        const paging = await loaderPaging(queryClient, params, 'my_feedback');
        await orNotFound(queryClient.query(myFeedbackQuery(myFeedbackView(params), paging)));
        return null;
      },
      shouldRevalidate: sizeSaved,
      lazy: async () => ({
        Component: (await import('./feedback/MyFeedbackPage')).MyFeedbackPage,
      }),
      handle: {
        ...myFeedbackHelp,
        search: {
          label: 'My feedback',
          meta: 'What you sent with the Feedback button, where it stands, and the MyRewards team’s replies',
          visible: settingsArea('my-feedback'),
        },
      },
    },
    {
      path: 'settings/me/feedback/:displayId',
      loader: async ({ request, params }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        const displayId = params['displayId'] ?? '';
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router's 404 response
        if (!DISPLAY_ID.test(displayId)) throw notFound();
        if (me.preview !== null) return null;
        // Reading it marks the team's replies as seen: the badge and the list follow.
        const report = await orNotFound(queryClient.query(myFeedbackItemQuery(displayId)));
        if (report.conversation.some((entry) => entry.type === 'team' && entry.new)) {
          void queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
          void queryClient.invalidateQueries({ queryKey: myFeedbackKeys.lists });
        }
        return null;
      },
      lazy: async () => ({
        Component: (await import('./feedback/MyFeedbackItemPage')).MyFeedbackItemPage,
      }),
      handle: myFeedbackHelp,
    },
    {
      path: 'settings/budgets',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        if (canOpenArea(me, 'budgets')) await quietly(queryClient.query(budgetsQuery));
        return null;
      },
      lazy: async () => {
        const { SettingsBudgetPoolsPage } = await import('./BudgetPoolsPage');
        return { Component: SettingsBudgetPoolsPage };
      },
      handle: help('Settings · Budgets', ['budget-ledger', 'pool', 'settings-history'], {
        label: 'Budget pools',
        meta: 'Each pool’s budget for the year, and its ledger',
        visible: settingsArea('budgets', true),
      }),
    },
    {
      path: 'settings/values',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        if (canOpenArea(me, 'values'))
          await warm(queryClient, 'values.fmvCents', () =>
            queryClient.query(settingQuery('values.fmvCents')),
          );
        return null;
      },
      lazy: async () => {
        const { SettingsValuesPage } = await import('./ValuesPage');
        return { Component: SettingsValuesPage };
      },
      handle: help('Settings · Values', ['fmv', 'settings-history'], {
        label: 'Values',
        meta: 'FMV per share, the PAA split and minimum options',
        visible: settingsArea('values', true),
      }),
    },
    {
      path: 'settings/charge-codes',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        if (canOpenArea(me, 'charge-codes'))
          await Promise.all([
            warm(queryClient, 'coding.accounts', () =>
              queryClient.query(settingQuery('coding.accounts')),
            ),
            quietly(queryClient.query(budgetsQuery)),
          ]);
        return null;
      },
      lazy: async () => {
        const { ChargeCodesPage } = await import('./ChargeCodesPage');
        return { Component: ChargeCodesPage };
      },
      handle: help('Settings · Charge codes', ['charge-codes', 'settings-history'], {
        label: 'Charge codes',
        meta: 'Accounts, pool projects and the org rule',
        visible: settingsArea('charge-codes'),
      }),
    },
    {
      path: 'settings/delegates',
      lazy: async () => {
        const { ExecutiveDelegatesPage } = await import('./ExecutiveDelegatesPage');
        return { Component: ExecutiveDelegatesPage };
      },
      handle: help('Executive delegates', ['requesters'], {
        label: 'Executive delegates',
        meta: 'Who enters awards for an Executive',
        visible: settingsArea('executive-delegates'),
      }),
    },
    {
      path: 'settings/sector',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        if (canOpenArea(me, 'sector')) await quietly(queryClient.query(sectorSetupListQuery));
        return null;
      },
      lazy: async () => {
        const { SectorSettingsPage } = await import('./SectorSettingsPage');
        return { Component: SectorSettingsPage };
      },
      handle: help('Sector settings', ['delegates', 'programs', 'delegate-entry'], {
        label: 'Sector settings',
        meta: 'Delegates and programs',
        visible: settingsArea('sector'),
      }),
    },
    {
      path: 'settings/elections-letters',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        // APP-130 D96: a letter cell opens on the Letters page now. Links made before it (a gap
        // card's, a bookmark) carry `?cell=` here, and land on the same cell there.
        // Only the cell goes on (SENT-053).
        const cell = new URL(request.url).searchParams.get('cell');
        if (cell !== null)
          // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router loaders redirect by throwing a Response.
          throw redirect(letterCellHref(cell));
        // The Letters card here reads the coverage only: its counts and the CEO / President.
        if (canOpenArea(me, 'elections-letters')) {
          const access = await queryClient.query(settingsAccessQuery).catch(() => null);
          if (access === null || access.readable.includes('letters.templates'))
            await quietly(queryClient.query(letterCoverageQuery));
        }
        return null;
      },
      lazy: async () => {
        const { ElectionsLettersPage } = await import('./ElectionsLettersPage');
        return { Component: ElectionsLettersPage };
      },
      handle: help(
        'Settings · Elections and letters',
        ['letter-templates', 'election', 'letters'],
        {
          label: 'Elections and letters',
          meta: 'The election window, reminders and letter templates',
          visible: settingsArea('elections-letters'),
        },
      ),
    },
    {
      // APP-130 D96: Letters on a page of its own, outside the Settings frame. Its Help is
      // Elections and letters' (the `elections-letters` page key covers this address).
      path: 'settings/elections-letters/letters',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        if (canOpenArea(me, 'elections-letters')) {
          const access = await queryClient.query(settingsAccessQuery).catch(() => null);
          await Promise.all([
            access === null ||
            (access.letterTemplatesReadable ?? access.readable.includes('letters.templates'))
              ? quietly(queryClient.query(letterTemplatesQuery))
              : null,
            access === null || access.readable.includes('letters.templates')
              ? quietly(queryClient.query(letterCoverageQuery))
              : null,
          ]);
        }
        return null;
      },
      lazy: async () => {
        const { LettersPage } = await import('./letters/LettersPage');
        return { Component: LettersPage };
      },
      handle: help(
        'Settings · Elections and letters · Letters',
        ['letter-templates', 'election', 'letters'],
        {
          label: 'Letters',
          meta: 'A letter for each signer and cash and options mix: upload, review and approve',
          visible: settingsArea('elections-letters'),
        },
      ),
    },
    {
      path: 'settings/award-rules',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        if (canOpenArea(me, 'award-rules')) await quietly(queryClient.query(settingsAccessQuery));
        return null;
      },
      lazy: async () => ({ Component: (await import('./AwardRulesPage')).AwardRulesPage }),
      handle: help('Settings · Award rules', ['settings-history'], {
        label: 'Award rules',
        meta: 'Award types, funding, fiscal year and award defaults',
        visible: settingsArea('award-rules'),
      }),
    },
    {
      path: 'settings/spot',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        if (canOpenArea(me, 'spot-setup'))
          await Promise.all([
            quietly(queryClient.query(settingQuery('spot.workflow'))),
            quietly(queryClient.query(spotWorkflowSetupQuery)),
          ]);
        return null;
      },
      lazy: async () => ({ Component: (await import('./SpotSettingsPage')).SpotSettingsPage }),
      handle: help('Settings · Spot Award setup', ['spot-setup', 'spot'], {
        label: 'Spot Award setup',
        meta: 'Who reviews Spot nominations, and the funding pool',
        visible: settingsArea('spot-setup'),
      }),
    },
    {
      path: 'settings/payroll',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        if (canOpenArea(me, 'payroll'))
          await warm(queryClient, 'payroll.earningStatementRequired', () =>
            queryClient.query(settingQuery('payroll.earningStatementRequired')),
          );
        return null;
      },
      lazy: async () => {
        const { PayrollSettingsPage } = await import('./PayrollSettingsPage');
        return { Component: PayrollSettingsPage };
      },
      handle: help('Settings · Payroll', ['tax-estimate', 'settings-history'], {
        label: 'Payroll settings',
        meta: 'Where tax figures come from and the earning statement rule',
        visible: settingsArea('payroll'),
      }),
    },
    {
      // APP-101: the Tax estimate page became Payroll; old links and bookmarks land there.
      path: 'settings/tax-estimate',
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requireMe(queryClient, request);
        // eslint-disable-next-line @typescript-eslint/only-throw-error -- React Router loaders redirect by throwing a Response.
        throw redirect('/settings/payroll');
      },
    },
  ];
}

/**
 * Manage's Executive group's own pages (X4, X5): Budget pools and Values & requesters. APP-236
 * moved them from `/executive/…` to `/manage/executive/…`; each old address is a redirect-only
 * route keeping its query and hash, so emails, Help's Start buttons, Ask the data's links and
 * bookmarks still land, in a sample preview too.
 */
export function executiveRoutes(queryClient: QueryClient): RouteObject[] {
  return [
    movedTo('executive', MANAGE_PAGE['budget-pools'].href),
    movedTo('executive/budget-pools', MANAGE_PAGE['budget-pools'].href),
    movedTo('executive/values', MANAGE_PAGE.values.href),
    // The group's own address opens its first page, as `/executive` did.
    movedTo('manage/executive', MANAGE_PAGE['budget-pools'].href),
    {
      path: 'manage/executive/budget-pools',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        if (executive(me)) await quietly(queryClient.query(budgetsQuery));
        return null;
      },
      lazy: async () => {
        const { ExecutiveBudgetPoolsPage } = await import('./BudgetPoolsPage');
        return { Component: ExecutiveBudgetPoolsPage };
      },
      handle: help('Executive · Budget pools', ['budget-ledger', 'pool', 'settings-history'], {
        label: 'Budget pools',
        meta: 'Executive · each pool’s budget for the year, and its ledger',
        visible: executive,
      }),
    },
    {
      path: 'manage/executive/values',
      loader: async ({ request }: LoaderFunctionArgs) => {
        const me = await requireMe(queryClient, request);
        if (executive(me))
          await warm(queryClient, 'values.fmvCents', () =>
            queryClient.query(settingQuery('values.fmvCents')),
          );
        return null;
      },
      lazy: async () => {
        const { ExecutiveValuesPage } = await import('./ValuesPage');
        return { Component: ExecutiveValuesPage };
      },
      handle: help('Executive · Values', ['fmv', 'settings-history'], {
        label: 'Values',
        meta: 'Executive · FMV, the PAA split and who can request',
        visible: executive,
      }),
    },
  ];
}
